import { useState, useEffect } from 'react'
import { Megaphone, Image as ImageIcon, Loader2, X, Check } from 'lucide-react'
import toast from 'react-hot-toast'
import { db, storage } from '../../config/firebase'
import { addDoc, collection, serverTimestamp, getDocs, query, where, doc, getDoc } from 'firebase/firestore'
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage'
import { useAuthStore } from '../../store/authStore'

export default function BroadcastTab() {
    const { currentOrg } = useAuthStore()
    const [title, setTitle] = useState('')
    const [message, setMessage] = useState('')
    const [actionUrl, setActionUrl] = useState('')
    const [targetGroup, setTargetGroup] = useState<'all' | 'admins' | 'members'>('all')
    const [targetUserIds, setTargetUserIds] = useState<string[]>([])
    const [members, setMembers] = useState<any[]>([])
    const [imageUrl, setImageUrl] = useState('')
    const [loading, setLoading] = useState(false)
    const [uploadingImage, setUploadingImage] = useState(false)

    const toggleMember = (uid: string) => {
        setTargetUserIds(prev =>
            prev.includes(uid) ? prev.filter(id => id !== uid) : [...prev, uid]
        )
    }

    const toggleAllMembers = () => {
        if (targetUserIds.length === members.length) {
            setTargetUserIds([])
        } else {
            setTargetUserIds(members.map(m => m.id))
        }
    }

    // Fetch members when targetGroup is 'members'
    useEffect(() => {
        const fetchMembers = async () => {
            if (targetGroup === 'members' && currentOrg) {
                try {
                    // Correctly fetch members from the 'members' collection
                    const q = query(collection(db, 'members'), where('orgId', '==', currentOrg.id))
                    const snapshot = await getDocs(q)
                    const memberUserIds = snapshot.docs.map(doc => doc.data().userId)

                    if (memberUserIds.length > 0) {
                        const usersPromises = memberUserIds.map(uid => getDoc(doc(db, 'users', uid)))
                        const userSnapshots = await Promise.all(usersPromises)
                        const usersData = userSnapshots
                            .filter(snap => snap.exists())
                            .map(snap => ({ id: snap.id, ...snap.data() }))
                        setMembers(usersData)
                    } else {
                        setMembers([])
                    }
                } catch (error) {
                    console.error("Error fetching members:", error)
                    toast.error("Erro ao carregar lista de membros")
                }
            }
        }
        fetchMembers()
    }, [targetGroup, currentOrg])

    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (!file || !currentOrg) return

        setUploadingImage(true)
        try {
            const storageRef = ref(storage, `broadcasts/${currentOrg.id}/${Date.now()}_${file.name}`)
            const snapshot = await uploadBytes(storageRef, file)
            const url = await getDownloadURL(snapshot.ref)
            setImageUrl(url)
            toast.success("Imagem carregada!")
        } catch (error) {
            console.error("Error uploading image:", error)
            toast.error("Erro ao carregar imagem")
        } finally {
            setUploadingImage(false)
        }
    }

    const handleSend = async () => {
        if (!title || !message) {
            toast.error('Preencha título e mensagem.')
            return
        }

        if (targetGroup === 'members' && targetUserIds.length === 0) {
            toast.error('Selecione pelo menos um membro.')
            return
        }

        setLoading(true)
        try {
            await addDoc(collection(db, 'notifications_broadcast'), {
                title,
                message,
                targetGroup,
                targetUserIds: targetGroup === 'members' ? targetUserIds : null,
                imageUrl: imageUrl || null,
                actionUrl: actionUrl || null,
                createdAt: serverTimestamp(),
                status: 'pending'
            })

            toast.success('Broadcast enviado com sucesso!')
            setTitle('')
            setMessage('')
            setImageUrl('')
            setActionUrl('')
            setTargetUserIds([])
            setTargetGroup('all')
        } catch (error) {
            console.error(error)
            toast.error('Erro ao enviar broadcast.')
        } finally {
            setLoading(false)
        }
    }

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Form */}
            <div className="bg-white rounded-[2rem] p-8 border border-[#001c28]/5 shadow-sm">
                <h3 className="text-xl font-black text-[#001c28] uppercase italic mb-6 flex items-center gap-2">
                    <Megaphone className="text-[#d4a94d]" />
                    Nova Transmissão
                </h3>

                <div className="space-y-4">
                    <div>
                        <label className="block text-xs font-bold text-[#001c28] uppercase mb-1 ml-1">Para quem?</label>
                        <div className="flex bg-[#001c28]/5 p-1 rounded-xl mb-4">
                            {(['all', 'members', 'admins'] as const).map((group) => (
                                <button
                                    key={group}
                                    onClick={() => setTargetGroup(group)}
                                    className={`flex-1 py-2 rounded-lg text-xs font-black uppercase transition-all ${targetGroup === group
                                        ? 'bg-white text-[#001c28] shadow-sm'
                                        : 'text-[#001c28]/40 hover:text-[#001c28]'
                                        }`}
                                >
                                    {group === 'all' ? 'Todos' : group === 'members' ? 'Membro' : 'Admins'}
                                </button>
                            ))}
                        </div>

                        {targetGroup === 'members' && (
                            <div className="animate-in slide-in-from-top-2 duration-300">
                                <div className="flex justify-between items-center mb-2">
                                    <label className="block text-xs font-bold text-[#001c28] uppercase ml-1">Selecione os Membros ({targetUserIds.length})</label>
                                    <button onClick={toggleAllMembers} className="text-[10px] text-[#d4a94d] font-bold uppercase hover:underline">
                                        {targetUserIds.length === members.length ? 'Desmarcar Todos' : 'Marcar Todos'}
                                    </button>
                                </div>
                                <div className="max-h-48 overflow-y-auto bg-[#001c28]/5 border border-[#001c28]/10 rounded-xl p-2 space-y-1">
                                    {members.map(member => (
                                        <button
                                            key={member.id}
                                            onClick={() => toggleMember(member.id)}
                                            className={`w-full flex items-center gap-3 p-2 rounded-lg text-left transition-all ${targetUserIds.includes(member.id)
                                                ? 'bg-white shadow-sm ring-1 ring-[#d4a94d]'
                                                : 'hover:bg-white/50'
                                                }`}
                                        >
                                            <div className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${targetUserIds.includes(member.id) ? 'bg-[#d4a94d] border-[#d4a94d]' : 'border-gray-400'
                                                }`}>
                                                {targetUserIds.includes(member.id) && <Check size={10} className="text-[#001c28]" />}
                                            </div>
                                            <span className="text-xs font-bold text-[#001c28]">
                                                {member.displayName || member.email || 'Usuário sem nome'}
                                            </span>
                                        </button>
                                    ))}
                                    {members.length === 0 && (
                                        <p className="text-center text-[10px] text-gray-400 py-2">Nenhum membro encontrado.</p>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    <div>
                        <label className="block text-xs font-bold text-[#001c28] uppercase mb-1 ml-1">Título</label>
                        <input
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="Ex: Novo Horário de Funcionamento"
                            className="w-full bg-[#001c28]/5 border border-[#001c28]/10 rounded-xl px-4 py-3 text-[#001c28] font-bold focus:outline-none focus:border-[#d4a94d]"
                        />
                    </div>

                    <div>
                        <label className="block text-xs font-bold text-[#001c28] uppercase mb-1 ml-1">Mensagem</label>
                        <textarea
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            placeholder="Escreva sua mensagem aqui..."
                            rows={4}
                            className="w-full bg-[#001c28]/5 border border-[#001c28]/10 rounded-xl px-4 py-3 text-[#001c28] font-bold focus:outline-none focus:border-[#d4a94d] resize-none"
                        />
                    </div>

                    <div>
                        <label className="block text-xs font-bold text-[#001c28] uppercase mb-1 ml-1">Link de Ação (Opcional)</label>
                        <input
                            type="text"
                            value={actionUrl}
                            onChange={(e) => setActionUrl(e.target.value)}
                            placeholder="Ex: /bookings/catalog ou https://..."
                            className="w-full bg-[#001c28]/5 border border-[#001c28]/10 rounded-xl px-4 py-3 text-[#001c28] font-bold focus:outline-none focus:border-[#d4a94d]"
                        />
                    </div>

                    <div>
                        <label className="block text-xs font-bold text-[#001c28] uppercase mb-1 ml-1">Imagem</label>
                        {imageUrl ? (
                            <div className="relative rounded-xl overflow-hidden border border-[#001c28]/10 h-40 group">
                                <img src={imageUrl} alt="Preview" className="w-full h-full object-cover" />
                                <button
                                    onClick={() => setImageUrl('')}
                                    className="absolute top-2 right-2 bg-black/50 hover:bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-all"
                                >
                                    <X size={16} />
                                </button>
                            </div>
                        ) : (
                            <label className="flex flex-col items-center justify-center h-24 border-2 border-dashed border-[#001c28]/10 rounded-xl hover:bg-[#001c28]/5 cursor-pointer transition-all gap-2">
                                {uploadingImage ? (
                                    <Loader2 className="animate-spin text-[#d4a94d]" />
                                ) : (
                                    <>
                                        <ImageIcon className="text-[#001c28]/20" />
                                        <span className="text-[10px] font-bold text-[#001c28]/40 uppercase">Clique para adicionar imagem</span>
                                    </>
                                )}
                                <input type="file" className="hidden" accept="image/*" onChange={handleImageUpload} disabled={uploadingImage} />
                            </label>
                        )}
                    </div>
                </div>

                <div className="mt-8 pt-6 border-t border-[#001c28]/5 flex justify-end">
                    <button
                        onClick={handleSend}
                        disabled={loading || uploadingImage}
                        className="bg-[#001c28] text-white px-8 py-3 rounded-xl font-black uppercase tracking-widest hover:bg-[#001c28]/90 transition-all shadow-lg shadow-[#001c28]/20 flex items-center gap-2 disabled:opacity-50"
                    >
                        {loading ? 'Enviando...' : (
                            <>
                                <Megaphone size={18} className="text-[#d4a94d]" />
                                Enviar Agora
                            </>
                        )}
                    </button>
                </div>
            </div>

            {/* Preview */}
            <div className="space-y-6">
                <h3 className="text-sm font-black text-[#001c28] uppercase tracking-widest opacity-50">Preview</h3>

                {/* Device Mockup */}
                <div className="bg-white rounded-3xl overflow-hidden shadow-2xl border-8 border-gray-900 max-w-sm mx-auto relative">
                    <div className="bg-gray-900 h-6 w-32 mx-auto rounded-b-xl absolute top-0 left-1/2 -translate-x-1/2 z-10"></div>
                    <div className="bg-[#f2f2f2] h-[500px] pt-12 px-4 pb-4 overflow-y-auto">
                        {/* Notification Card */}
                        <div className="bg-white p-4 rounded-2xl shadow-sm border border-black/5 animate-in slide-in-from-top-4 duration-500">
                            <div className="flex items-start gap-3">
                                <div className="w-10 h-10 bg-[#001c28] rounded-xl flex items-center justify-center shrink-0">
                                    <img src="/logo.png" className="w-6 h-6 object-contain brightness-0 invert" alt="" />
                                </div>
                                <div className="flex-1">
                                    <div className="flex justify-between items-start">
                                        <p className="text-[#001c28] font-bold text-sm">SpaceRub</p>
                                        <span className="text-[10px] text-gray-400">Agora</span>
                                    </div>
                                    <p className="text-[#001c28] font-bold text-xs mt-1">{title || 'Título da Notificação'}</p>
                                    <p className="text-gray-500 text-xs mt-1 leading-relaxed">
                                        {message || 'O corpo da sua mensagem aparecerá aqui.'}
                                    </p>
                                    {imageUrl && (
                                        <div className="mt-3 rounded-lg overflow-hidden h-32 w-full bg-gray-100">
                                            <img src={imageUrl} className="w-full h-full object-cover" alt="Preview" />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
